import { Badge, Button, Spinner } from '@ui';
import { useCallback, useEffect, useState } from 'react';
import { bugApi, type ThemesView as Themes } from '../bugApi';
import { RefreshIcon, RepeatIcon } from '../icons';

// Пов12 (вид — 👁, сверка с владельцем): «Сводка по темам» — строка на тему (репорты, связанные повторами), столбцы — последние 14 дней,
// цвет ячейки — число репортов, подпись — интенсивность, рядом с названием — вердикт. Одна раскладка на любой ширине: полоса дней тянется.

const VERDICTS = {
  repeats: { text: 'повторяется', tone: 'warning' },
  stopped: { text: 'перестал', tone: 'success' },
  continues: { text: 'продолжается', tone: 'error' },
} as const;

/** The heat step of a cell: 0 — none, 1..4 — more reports. */
export const heatOf = (count: number): number => Math.min(4, count);

const dayLabel = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

export function ThemesView({ onError }: { onError: (err: unknown, fallback: string) => void }) {
  const [data, setData] = useState<Themes | null>(null);

  const load = useCallback(() => {
    bugApi
      .themes()
      .then(setData)
      .catch((err) => onError(err, 'Не удалось загрузить сводку по темам'));
  }, [onError]);

  useEffect(load, [load]);

  if (!data) return <Spinner label="Загрузка сводки" />;
  const first = data.days[0];
  const last = data.days[data.days.length - 1];

  return (
    <div className="themes" data-testid="themes">
      <div className="board-toolbar">
        <span className="themes-axis jui-muted">
          <span>{first ? dayLabel(first) : ''}</span>
          <span>14 дней</span>
          <span>{last ? dayLabel(last) : ''}</span>
        </span>
        <Button size="sm" variant="quiet" icon={<RefreshIcon />} onClick={load}>
          Обновить
        </Button>
      </div>
      {data.rows.length === 0 && <div className="jui-muted">Репортов нет</div>}
      {data.rows.map((row) => (
        <div key={row.key} className="themes-row" data-testid="themes-row" data-verdict={row.verdict ?? 'none'}>
          <div className="themes-head">
            <RepeatIcon />
            <span className="themes-title" data-pii="">
              {row.title}
            </span>
            {row.verdict && (
              <Badge tone={VERDICTS[row.verdict].tone}>
                <span data-bug-open="">{VERDICTS[row.verdict].text}</span>
              </Badge>
            )}
          </div>
          <div className="themes-strip" role="img" aria-label={`Репортов по дням: ${row.perDay.join(', ')}`}>
            {row.perDay.map((count, i) => (
              <span key={data.days[i]} className={`themes-cell heat-${heatOf(count)}`} title={`${dayLabel(data.days[i])}: ${count}`} data-bug-open="">
                {count || ''}
              </span>
            ))}
          </div>
          <div className="themes-foot jui-muted" data-bug-open="">
            <span>{row.total} репортов</span>
            <span>{String(row.rate).replace('.', ',')} в день</span>
            <span className="themes-numbers">
              {row.numbers
                .slice(0, 8)
                .map((n) => `№${n}`)
                .join(' ')}
              {row.numbers.length > 8 ? ` +${row.numbers.length - 8}` : ''}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
